import { FC, useEffect, useState } from 'react';

import cx from 'classnames';

import { SearchIcon } from 'assets/icons';
import { If } from 'components/Generics';
import OutsideClickHandler from 'components/OutsideClickHandler/OutsideClickHandler';
import { translate } from 'utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LookupOption {
  id: string;
  name: string;
  /** Optional account currency when the suggestion is an account lookup */
  currencyIsoCode?: string | null;
}

export interface LookupFieldProps {
  /** Form field label */
  label: string;
  required?: boolean;
  /** Currently stored ID value */
  value: string;
  /**
   * Display name for the currently stored ID.
   * Pass the human-readable name you want shown in the input on initial render
   * (e.g. `lead.leadOwner` for the owner field in edit mode).
   */
  displayName?: string;
  /** Called when the user selects a suggestion — receives id, name, and the full option */
  onChange: (id: string, name: string, option: LookupOption) => void;
  /** Called when the user clears the field */
  onClear: () => void;
  /** Called when the user clicks Search (or presses Enter). Parent should call the API here. */
  fetchSuggestions: (search: string) => void;
  suggestions: LookupOption[];
  loading?: boolean;
  error?: string;
  placeholder?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const LookupField: FC<LookupFieldProps> = ({
  label,
  required,
  value,
  displayName = '',
  onChange,
  onClear,
  fetchSuggestions,
  suggestions,
  loading = false,
  error,
  placeholder
}) => {
  const [inputText, setInputText] = useState(displayName);
  const [open, setOpen] = useState(false);
  // Tracks the display name of the last confirmed selection so we can detect
  // when the user edits the text away from it without picking a new option.
  const [confirmedName, setConfirmedName] = useState(displayName);
  // Tracks which query was last searched vs which query the current suggestions
  // belong to, so stale results from a previous search are never shown.
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);
  const [resolvedQuery, setResolvedQuery] = useState<string | null>(null);

  const isResultsStale = searchedQuery !== resolvedQuery;

  // Keep input in sync when the parent updates the display name (e.g. on reset)
  useEffect(() => {
    setInputText(displayName);
    setConfirmedName(displayName);
    setSearchedQuery(null);
    setResolvedQuery(null);
    setOpen(false);
  }, [displayName]);

  useEffect(() => {
    if (!loading && searchedQuery !== null) {
      setResolvedQuery(searchedQuery);
    }
  }, [loading, searchedQuery]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setInputText(text);
    setOpen(false);

    if (text.trim()) {
      // If the user has edited away from the confirmed selection, clear the
      // stored ID so a stale ID is never submitted.
      if (value && text !== confirmedName) {
        onClear();
      }
    } else {
      onClear();
    }
  };

  const handleSearch = () => {
    const search = inputText.trim();
    if (!search) return;
    setResolvedQuery(null);
    setSearchedQuery(search);
    fetchSuggestions(search);
    setOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSelect = (option: LookupOption) => {
    setInputText(option.name);
    setConfirmedName(option.name);
    setOpen(false);
    onChange(option.id, option.name, option);
  };

  const handleClear = () => {
    setInputText('');
    setConfirmedName('');
    setSearchedQuery(null);
    setResolvedQuery(null);
    setOpen(false);
    onClear();
  };

  return (
    <div className='flex flex-col'>
      <label className='text-xs text-DISABLED mb-1'>
        {label}
        {required && <span className='text-ERROR ml-0.5'>*</span>}
      </label>

      <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
        <div className='flex gap-2'>
          <div className='relative flex-1'>
            <input
              type='text'
              className={cx(
                'w-full border rounded-lg px-3 py-2.5 pr-8 text-sm text-DEFAULT_TEXT focus:outline-none focus:border-PRIMARY',
                error ? 'border-ERROR' : 'border-LIGHT_GRAY'
              )}
              value={inputText}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder ?? label}
            />

            {(inputText || value) && (
              <button
                type='button'
                onClick={handleClear}
                className='absolute right-2 top-1/2 -translate-y-1/2 text-DISABLED hover:text-DEFAULT_TEXT'
                aria-label={translate('common.clear')}
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            )}

            <If condition={open}>
              <div className='absolute z-50 w-full mt-1 bg-white border border-LIGHT_GRAY rounded-lg shadow-lg max-h-48 overflow-y-auto'>
                {loading || isResultsStale ? (
                  <div className='px-3 py-2 text-sm text-DISABLED'>
                    {translate('patient_list.loading_data')}
                  </div>
                ) : suggestions.length === 0 ? (
                  <div className='px-3 py-2 text-sm text-DISABLED'>
                    {translate('common.no_results')}
                  </div>
                ) : (
                  suggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      type='button'
                      className='w-full text-left px-3 py-2 text-sm hover:bg-NEUTRAL_50 text-DEFAULT_TEXT'
                      onClick={() => handleSelect(suggestion)}
                    >
                      {suggestion.name}
                    </button>
                  ))
                )}
              </div>
            </If>
          </div>

          <button
            type='button'
            onClick={handleSearch}
            disabled={!inputText.trim() || loading || isResultsStale}
            className={cx(
              'shrink-0 flex items-center gap-1.5 border rounded-lg px-3 py-2.5 text-sm',
              'border-LIGHT_GRAY text-DEFAULT_TEXT hover:border-PRIMARY hover:text-PRIMARY',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-LIGHT_GRAY disabled:hover:text-DEFAULT_TEXT'
            )}
            aria-label={translate('search.search')}
          >
            <SearchIcon className='w-4 h-4' />
            <span className='hidden sm:inline'>
              {translate('search.search')}
            </span>
          </button>
        </div>
      </OutsideClickHandler>

      {error && <span className='text-xs text-ERROR mt-1'>{error}</span>}
    </div>
  );
};

export default LookupField;
